/**
 * The application's version as the owner sees it: „v102” = the PR whose squash commit is on main (deploy/app-version.sh).
 * The same shape as GET /api/version.
 */
export type AppBuild = { version: string | null; commit: string | null; builtAt: string | null };

/** Short label at the top: „v102”; a build without a number (dev, a manual build): „dev”. */
export function versionLabel(b: AppBuild): string {
  return b.version ? `v${b.version}` : 'dev';
}

// The firm works in Poland: the build time always in Polish time, whatever the device's zone.
const BUILT = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** „23.10 10:40” in Polish time, or null for a bad date. */
function shortTime(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = Object.fromEntries(BUILT.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.day}.${p.month} ${p.hour}:${p.minute}`;
}

function builtText(iso: string): string | null {
  const t = shortTime(iso);
  return t ? `zbudowana ${t}` : null;
}

/** „wersja 102 · zbudowana 23.10 10:40 · a1b2c3d”. */
export function versionDetails(b: AppBuild): string {
  if (!b.version) return 'wersja robocza (bez numeru)';
  const built = b.builtAt ? builtText(b.builtAt) : null;
  return [`wersja ${b.version}`, built, b.commit].filter(Boolean).join(' · ');
}

/** The server runs another build than the one this page was loaded from (a server that knows no version: never). */
export function isNewer(current: AppBuild, server: AppBuild): boolean {
  return !!server.commit && server.commit !== current.commit;
}

/** One point of „Nowości” (docs/nowosci.md), with points nested under it. */
export type VersionNote = { text: string; items?: string[] };

/** One row of „Historia zmian” (GET /api/version/changes, made at deploy time from the squash commits on main). */
export type VersionChange = { version: number; at: string; title: string; notes?: VersionNote[] };

const isText = (v: unknown): v is string => typeof v === 'string';

function parseNotes(value: unknown): VersionNote[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const notes = value.flatMap((n): VersionNote[] => {
    if (typeof n !== 'object' || n === null || !isText((n as VersionNote).text)) return [];
    const items = (n as { items?: unknown }).items;
    const list = Array.isArray(items) ? items.filter(isText) : [];
    return [list.length ? { text: (n as VersionNote).text, items: list } : { text: (n as VersionNote).text }];
  });
  return notes.length ? notes : undefined;
}

/** The body of GET /api/version/changes; anything else: null. Bad rows (and bad „Nowości” points) are left out. */
export function parseChanges(body: unknown): VersionChange[] | null {
  if (typeof body !== 'object' || body === null) return null;
  const entries = (body as { entries?: unknown }).entries;
  if (!Array.isArray(entries)) return null;
  return entries.flatMap((e): VersionChange[] => {
    if (typeof e !== 'object' || e === null || typeof e.version !== 'number' || !isText(e.at) || !isText(e.title)) return [];
    const notes = parseNotes((e as { notes?: unknown }).notes);
    const row: VersionChange = { version: e.version, at: e.at, title: e.title };
    return [notes ? { ...row, notes } : row];
  });
}

/** „v127 · 23.10 21:31 · <tytuł>”. */
export function changeLine(c: VersionChange): string {
  return [`v${c.version}`, shortTime(c.at), c.title].filter(Boolean).join(' · ');
}
